import { sendUserMessage } from "./chat";
import { app } from "./app.svelte";

/**
 * Run the configured battery of standard questions sequentially in the
 * current conversation thread. Each question is dispatched through
 * `sendUserMessage`, which already handles RAG retrieval, streaming,
 * persistence, citations, etc. — so each Q&A lands in the conversation as
 * if the user had typed it.
 *
 * Stops on first error (per product decision). The user can interrupt the
 * run via the existing chat stop button: it aborts the in-flight chat turn,
 * `app.lastError` may stay null but `chatAbortController` was triggered, and
 * the next iteration sees `app.isStreaming` is false but the loop bails out
 * because we detect cancellation through a local flag flipped here.
 */
export async function runTestSuite(prefix = "", grid?: string[]): Promise<void> {
  const cfg = app.config;
  if (!cfg) return;
  // `grid` : une grille nommée choisie dans la v3 ; sinon la liste des réglages.
  const questions = grid ?? cfg.testSuite ?? [];
  if (questions.length === 0) return;
  if (app.testSuiteRunning || app.isStreaming) return;

  // The composer text (if any) is used as a one-off preamble prepended to the
  // first question actually sent. Subsequent questions go out as-is — they run
  // in the same conversation, so the model keeps the preamble's context.
  const preamble = prefix.trim();
  let preambleUsed = false;

  app.lastError = null;
  app.testSuiteRunning = true;
  app.testSuiteTotal = questions.length;
  app.testSuiteIndex = 0;

  try {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i].trim();
      if (!q) continue;
      app.testSuiteIndex = i + 1;

      let message = q;
      if (preamble && !preambleUsed) {
        message = `${preamble}\n\n${q}`;
        preambleUsed = true;
      }

      const errorBefore = app.lastError;
      await sendUserMessage(message);

      // sendUserMessage swallows AbortError silently and surfaces other
      // errors via app.lastError. Either signal stops the run.
      if (app.lastError && app.lastError !== errorBefore) return;
      if (app.testSuiteCancelled) return;
    }
  } finally {
    app.testSuiteRunning = false;
    app.testSuiteIndex = 0;
    app.testSuiteTotal = 0;
    app.testSuiteCancelled = false;
  }
}
